/* eslint-disable react/jsx-props-no-spreading */
import React, { useMemo } from 'react';
import {
  Autocomplete,
  Box,
  TextField,
  Typography,
} from '@mui/material';
import { UserStatus } from '@marageti/z4-sdk/lib/auth';
import { Agent } from '@marageti/z4-sdk/lib/people';
import { titleCase } from '../../utils/helpers';

const ALL_OPTION = { title: 'All', value: 'all' };
const NOT_ASSIGNED_OPTION = { title: 'Not Assigned', value: 'none' };

type SpecialistOption = {
  title: string;
  value: string;
  status?: string;
};

const SpecialistSelect = ({
  assignedTo,
  onChange,
  label = 'Specialist',
  agents,
}: {
  assignedTo: string,
  onChange: (v: string) => void,
  label?: string,
  agents: Agent[],
}) => {
  const agentsAsOptions = useMemo(() => {
    if (!agents) return [ALL_OPTION];
    const defOptions = [ALL_OPTION];
    const options: Record<UserStatus, SpecialistOption[]> = {
      ENABLED: [],
      PENDING: [],
      DISABLED: [],
    } as Record<UserStatus, SpecialistOption[]>;

    // Status field is used for option grouping and title
    agents.forEach((agent) => {
      options[agent.userInfo[0].status].push({
        title: agent.fullName,
        value: agent.id,
        status: agent.userInfo[0].status === UserStatus.ENABLED
          ? '--- Active ---' : `--- ${titleCase(agent.userInfo[0].status)} ---`,
      });
    });

    options.ENABLED.sort((a, b) => a.title.localeCompare(b.title));
    options.PENDING.sort((a, b) => a.title.localeCompare(b.title));
    options.DISABLED.sort((a, b) => a.title.localeCompare(b.title));

    defOptions.push(NOT_ASSIGNED_OPTION);

    return [...defOptions, ...options.ENABLED, ...options.PENDING, ...options.DISABLED];
  }, [agents]);

  const getSelectedSpecialist = (): SpecialistOption => {
    if (!assignedTo || assignedTo === 'all') {
      return ALL_OPTION;
    }

    if (assignedTo === 'none') {
      return NOT_ASSIGNED_OPTION;
    }

    const specialistName = agentsAsOptions?.find((op) => op.value === assignedTo)?.title || '';
    return specialistName
      ? { title: specialistName, value: assignedTo }
      : ALL_OPTION;
  };

  const selectedSpecialist = getSelectedSpecialist();

  return (
    <Autocomplete
      getOptionKey={(o) => o.value}
      getOptionLabel={(o) => o.title}
      // @ts-ignore
      groupBy={(o) => o.status || ''}
      id="specialist"
      isOptionEqualToValue={(o, v) => o.value === v.value}
      onChange={(_, v) => onChange(v?.value || '')}
      options={agentsAsOptions}
      renderInput={(params) => (
        <TextField
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...params}
          label={label}
          name={label}
        />
      )}
      disableClearable
      value={selectedSpecialist}
      fullWidth
      renderOption={({ key, ...props }, option) => (
        // @ts-ignore
        <Box {...props} key={key}>
          <Typography width="100%">{option.title}</Typography>
        </Box>
      )}
    />
  );
};

export default SpecialistSelect;
